import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, ThemePalette } from '@angular/material/core';
import { FloatLabelType, MatError, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';

// Value of the noneEnabled option.
export const YEAR_FILTER_NONE = -1;

// New-look replacement for app-payment-year-select. The list rebuilds when its range or omitValues change.
@Component({
  selector: 'app-year-filter-select',
  templateUrl: './year-filter-select.component.html',
  styleUrls: ['./year-filter-select.component.scss'],
  imports: [MatFormField, MatLabel, MatSelect, MatOption, MatError, ReactiveFormsModule]
})
export class YearFilterSelectComponent implements OnInit, OnChanges {

  readonly noneValue = YEAR_FILTER_NONE;

  private _defaultValue: number;
  @Input() set defaultValue(value: number) {
    this._defaultValue = value;
    this.control.setValue(value);
    this.defaultValueChanged.emit(value);
  }

  get defaultValue(): number {
    return this._defaultValue;
  }

  private _disabled = false;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    value ? this.control.disable() : this.control.enable();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Input() form: FormGroup;
  @Input() controlName = 'payment-year';
  @Input() color: ThemePalette = 'primary';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() label = 'Year';
  @Input() showLabel = true;
  @Input() placeholder = '';
  @Input() required = false;
  @Input() minYear = 2019;
  @Input() maxYear = new Date().getFullYear();
  @Input() maxYearAddition = 2;
  @Input() descSort = false;
  @Input() omitValues: number[] = [];
  @Input() noneEnabled = false;
  @Input() noneLabel = 'All';

  @Output() ready = new EventEmitter<void>();
  @Output() valueChanged = new EventEmitter<number>();
  @Output() defaultValueChanged = new EventEmitter<number>();

  options: number[] = [];
  readonly control = new FormControl<any>('');
  private initialized = false;

  ngOnInit(): void {
    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.setControl(this.controlName, this.control);

    this.getOptionData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && (changes['omitValues'] || changes['minYear'] || changes['maxYear'] || changes['maxYearAddition'] || changes['descSort'])) {
      this.buildOptions();
    }
  }

  getOptionData() {
    this.buildOptions();
    this.initialized = true;
    this.ready.emit();
  }

  setValue(value: number) {
    this.control.setValue(value);
  }

  // null when blank or set to the none option.
  year(): number | null {
    const value = this.control.value;
    return value && value != YEAR_FILTER_NONE ? Number(value) : null;
  }

  onSelectionChange() {
    this.valueChanged.emit(this.control.value);
  }

  private buildOptions() {
    const omit = new Set(this.omitValues ?? []);
    const options: number[] = [];

    for (let year = this.minYear; year <= this.maxYear + this.maxYearAddition; year++) {
      if (!omit.has(year)) {
        options.push(year);
      }
    }

    this.options = this.descSort ? options.reverse() : options;
  }
}
